// Function index:
//   mountQuestBoard(partyId) — wire the tray Board button + overlay. Lists the
//       Guild postings the acting character's rank may take, accepts one, and
//       points at the nearest Guild hall.
//
// The board is world-scoped and shared; what you may SEE of it is decided
// server-side from your Guild rank, and accept re-derives the same check. The
// client renders a decision, it never makes one.

import { getBoard, acceptPosting, type Advancement, type BoardPosting, type GuildExamState, type GuildHall } from '../api.js';

const RANK_TITLE: Record<string, string> = {
  E: 'Novice', D: 'Apprentice', C: 'Journeyman', B: 'Veteran', A: 'Elite', S: 'Legend',
};

export function mountQuestBoard(partyId: number): void {
  const btn = document.getElementById('board-btn') as HTMLButtonElement;
  const overlay = document.getElementById('board-overlay') as HTMLDivElement;
  const close = document.getElementById('board-close') as HTMLButtonElement;
  const body = document.getElementById('board-body') as HTMLDivElement;
  const rankEl = document.getElementById('board-rank') as HTMLSpanElement;

  function render(rank: string, postings: BoardPosting[], adv: Advancement | null, hall: GuildHall | null, exam: GuildExamState | null): void {
    rankEl.textContent = rank === 'none' ? '' : `${RANK_TITLE[rank] ?? rank} · ${advancementLine(adv, exam)}`;
    if (rank === 'none') {
      // Distinct from an empty board: the player needs to know WHICH it is.
      body.innerHTML = `<div class="board-empty">You are not a member of the Guild. Seek out a hall and ask to be sworn in.${hallLine(hall)}</div>`;
      return;
    }
    if (postings.length === 0) {
      body.innerHTML = `<div class="board-empty">Nothing posted at your rank right now.${hallLine(hall)}</div>`;
      return;
    }
    body.innerHTML = postings.map(renderPosting).join('') + hallFooter(hall);
  }

  async function refresh(): Promise<void> {
    try {
      const { rank, postings, advancement, hall, exam } = await getBoard(partyId);
      render(rank, postings, advancement ?? null, hall ?? null, exam ?? null);
    } catch {
      body.innerHTML = '<div class="board-empty">Could not read the board just now.</div>';
    }
  }

  btn.addEventListener('click', () => {
    overlay.hidden = false;
    body.innerHTML = '<div class="board-empty">loading…</div>';
    void refresh();
  });
  const hide = (): void => { overlay.hidden = true; };
  close.addEventListener('click', hide);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) hide(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !overlay.hidden) hide(); });

  // Delegated so buttons survive every re-render.
  body.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLButtonElement>('.board-accept');
    if (!el || el.disabled) return;
    el.disabled = true;
    void acceptPosting(partyId, Number(el.dataset.postingId))
      .then(() => refresh())
      .catch(() => { el.disabled = false; });
  });
}

// Shows what the next rank still costs, so a player is never left guessing why
// a promotion keeps being refused. The thresholds live server-side; this only
// renders the answer.
function advancementLine(adv: Advancement | null, exam: GuildExamState | null): string {
  if (!adv || !adv.nextRank) return 'showing your rank and below';
  const next = RANK_TITLE[adv.nextRank] ?? adv.nextRank;
  // Exam state outranks the thresholds: an eligible candidate mid-trial or in
  // a retry lockout needs THAT explained, not their quest count.
  if (exam?.open) return `${next} trial underway — finish it and report to the Examiner`;
  if (exam?.cooldown_until) return `exam cooldown — the Examiners will seat you again on ${exam.cooldown_until.slice(0, 10)}`;
  if (adv.eligible) return `ready to sit the ${next} exam at a Guild hall`;
  const q = adv.progress.quests;
  const m = adv.progress.merit;
  if (!q || !m) return 'showing your rank and below';
  return `${q.have}/${q.need} rank-${q.tier} quests · ${m.have}/${m.need} Merit to ${RANK_TITLE[adv.nextRank] ?? adv.nextRank}`;
}

// Where the Guild actually IS. Halls are derived from settlements server-side,
// so this is null in an interior — say nothing rather than point at a hall the
// player would have to leave the cave to reach.
function hallLine(hall: GuildHall | null): string {
  if (!hall) return '';
  return `<div class="board-hall">${esc(hall.label)} — ${whereFrom(hall)}</div>`;
}

function hallFooter(hall: GuildHall | null): string {
  if (!hall) return '';
  return `<div class="board-hall board-hall-foot">Nearest: ${esc(hall.label)} — ${whereFrom(hall)}</div>`;
}

function whereFrom(hall: GuildHall): string {
  if (hall.distance === 0) return 'you are here';
  const hexes = hall.distance === 1 ? '1 hex away' : `${Number(hall.distance)} hexes away`;
  return `${hexes} at (${Number(hall.q)}, ${Number(hall.r)})`;
}

function renderPosting(p: BoardPosting): string {
  const objs = p.objectives.map((o) => `<div class="obj"><span class="box">☐</span><span>${esc(o)}</span></div>`).join('');
  return `<div class="board-card${p.taken ? ' taken' : ''}">
    <span class="badge tier-${esc(p.tier)}">${esc(p.tier)}</span>
    <div class="title">${esc(p.title)}</div>
    <div class="summary">${esc(p.summary)}</div>
    ${objs}
    <div class="board-where">(${Number(p.q)}, ${Number(p.r)}) · ${Number(p.coin)} coin</div>
    <button type="button" class="board-accept" data-posting-id="${Number(p.id)}"${p.taken ? ' disabled' : ''}>${p.taken ? 'Taken' : 'Accept'}</button>
  </div>`;
}

// Every interpolated value is either escaped or numerically coerced. Titles
// come from the deterministic generator today, but a posting is world data and
// should not be trusted to stay that way.
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
